import * as React from 'react';
import * as t from 'io-ts';
import { SemibigHeader } from '@narvar/nth-bit-header';
import SpinnerIcon from '@narvar/nth-bit-icon-spinner';
import Paragraph from '@narvar/nth-bit-paragraph';
import Button from '@narvar/nth-block-button';
import { usePageContext } from '@narvar/nth-hook-context-page';
import TextLink from '@narvar/nth-bit-text-link';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import {
  useTranslations,
  withTranslationsProvider,
} from '@narvar/nth-hook-translations';
import {
  ActionsRow,
  CancelActionWrapper,
  ExpandActionWrapper,
  FedexAttributionWrapper,
  Item,
  List,
} from './components';
import { getLocations } from '../../../api/redirect-to-hold';
import {
  UI_STATE,
  useRedirectToHoldContext,
} from '../../../hooks/useRedirectToHoldState';
import PanelHeaderText from '../../PanelHeaderText';
import {
  CarrierKindV,
  LocationDetail,
  RequestRedirectToHoldLocations,
} from '../../../../server/api/schema';
import { decode } from '../../../../server/lib/codec';
import LocationItem from '../LocationItem';
import FedexAttribution from '../../FedexAttribution';
import ErrorAlert from '../../ErrorAlert';
import CustomComponentContext from '../../CustomComponentContext';

const LocationsInputsV = t.strict({
  carrier: CarrierKindV,
  trackingNumber: t.string,
  uniqueIdentifier: t.string,
});

const LIST_ITEM_COUNT_COLLAPSED = 3;
const LIST_ITEM_COUNT_EXPANDED = 10;

enum COMPONENT_STATE {
  LOADING,
  SUCCESS,
  SUCCESS_NO_DATA,
  ERRORED,
}

const SelectLocationPanel: React.FunctionComponent = () => {
  const { track } = React.useContext(CustomComponentContext);
  const { state: rthState, update: updateRthState } =
    useRedirectToHoldContext();
  const { state: pageContext } = usePageContext();
  const [locations, setLocations] = React.useState<LocationDetail[]>([]);
  const [selectedLocationId, setSelectedLocationId] = React.useState<
    string | null
  >(null);
  const [isListExpanded, setIsListExpanded] = React.useState(false);
  const [componentState, setComponentState] = React.useState<COMPONENT_STATE>(
    COMPONENT_STATE.LOADING,
  );

  const { address } = rthState;

  React.useEffect(() => {
    (async (): Promise<void> => {
      setComponentState(COMPONENT_STATE.LOADING);

      if (rthState.address === undefined) {
        setComponentState(COMPONENT_STATE.ERRORED);
        return;
      }

      const { carrier, trackingNumber, uniqueIdentifier } = decode(
        LocationsInputsV,
        {
          carrier: pageContext.carrier,
          trackingNumber: pageContext.trackingNumbers?.[0],
          uniqueIdentifier: rthState.uniqueIdentifier,
        },
      );

      const request: RequestRedirectToHoldLocations = {
        uniqueIdentifier,
        carrierMoniker: carrier,
        retailerMoniker: pageContext.retailerMoniker,
        // Set search distance to 50 miles since we only show the 10
        // nearest locations.
        searchDistance: 50,
        searchUnit: 'MI',
        addressVerification: rthState.address,
      };

      const locationsResponse = await getLocations({
        carrier,
        trackingNumber,
        request,
      });

      if (locationsResponse.success) {
        const slicedLocations = locationsResponse.locations.slice(
          0,
          LIST_ITEM_COUNT_EXPANDED,
        );
        setLocations(slicedLocations);
        setComponentState(
          slicedLocations.length > 0
            ? COMPONENT_STATE.SUCCESS
            : COMPONENT_STATE.SUCCESS_NO_DATA,
        );
      } else {
        setComponentState(COMPONENT_STATE.ERRORED);
      }
    })().catch(() => {
      setComponentState(COMPONENT_STATE.ERRORED);
    });
  }, [
    rthState.address,
    rthState.uniqueIdentifier,
    pageContext.carrier,
    pageContext.retailerMoniker,
    pageContext.trackingNumbers,
  ]);

  const {
    redirectToHoldStep2,
    redirectToHoldSelectLocationHeader,
    redirectToHoldLocationsNearbyTemplate,
    redirectToHoldSystemError,
    redirectToHoldErrorMissingAddress,
    redirectToHoldBestEffortWarning,
    redirectToHoldVerify,
    redirectToHoldCancel,
    redirectToHoldShowLess,
    redirectToHoldShowMore,
    redirectToHoldSelectLocation,
    redirectToHoldMonday,
    redirectToHoldTuesday,
    redirectToHoldWednesday,
    redirectToHoldThursday,
    redirectToHoldFriday,
    redirectToHoldSaturday,
    redirectToHoldSunday,
    redirectToHoldClosedAllDay,
    redirectToHoldOpenAllDay,
  } = useTranslations();

  const locationHourItemTranslation = {
    monday: redirectToHoldMonday ?? '',
    tuesday: redirectToHoldTuesday ?? '',
    wednesday: redirectToHoldWednesday ?? '',
    thursday: redirectToHoldThursday ?? '',
    friday: redirectToHoldFriday ?? '',
    saturday: redirectToHoldSaturday ?? '',
    sunday: redirectToHoldSunday ?? '',
    closedAllDay: redirectToHoldClosedAllDay ?? '',
    openAllDay: redirectToHoldOpenAllDay ?? '',
  };

  const logRedirectToHoldEvent = (opts: { clickSubType: string }) => {
    track({
      eventType: 'click',
      ...opts,
    });
  };

  const handleChooseLocation = (l: LocationDetail) => {
    logRedirectToHoldEvent({
      clickSubType: 'select_location',
    });

    updateRthState({
      ...rthState,
      uiState: UI_STATE.CONFIRM_LOCATION,
      selectedLocation: l,
    });
  };

  const handleCancel = () => {
    logRedirectToHoldEvent({
      clickSubType: 'cancel',
    });

    updateRthState({
      ...rthState,
      uiState: UI_STATE.INITIAL,
    });
  };

  const handleExpand = () => {
    logRedirectToHoldEvent({
      clickSubType: isListExpanded ? 'collapse' : 'expand',
    });
    setIsListExpanded(!isListExpanded);
  };

  const handleSelectItem = (l: LocationDetail): void => {
    logRedirectToHoldEvent({ clickSubType: 'expand_list_item' });
    setSelectedLocationId(l.id);
  };

  const visibleListItemCount = isListExpanded
    ? LIST_ITEM_COUNT_EXPANDED
    : LIST_ITEM_COUNT_COLLAPSED;

  const shouldShowExpandAction =
    componentState === COMPONENT_STATE.SUCCESS &&
    locations.length > LIST_ITEM_COUNT_COLLAPSED;

  const {
    linksNoneDefault,
    linksNoneHover,
    linksTrackingDefault,
    linksTrackingHover,
  } = useTheme();

  // For this component to be valid, a given address must exist
  // If not, then show an error UI to go back and verify an address
  if (address === undefined)
    return (
      <>
        <ErrorAlert
          show
          focusOnShow
          error
          dismissible={false}
          role="alert"
          timeout={0}
          s={{
            padding: '1rem',
            margin: '1rem 0',
            borderRadius: '.25rem',
          }}
        >
          <Paragraph containerCustomStyle={{ margin: 0, color: '#000' }}>
            {redirectToHoldErrorMissingAddress}
          </Paragraph>
        </ErrorAlert>
        <Button
          onClick={() =>
            updateRthState({ ...rthState, uiState: UI_STATE.VERIFY_ADDRESS })
          }
        >
          {redirectToHoldVerify}
        </Button>
      </>
    );

  let content = null;
  switch (componentState) {
    case COMPONENT_STATE.LOADING:
      content = (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem 0',
          }}
        >
          <SpinnerIcon />
        </div>
      );
      break;
    case COMPONENT_STATE.SUCCESS:
      content = (
        <>
          <Paragraph containerCustomStyle={{ margin: '1rem 0 1.5rem 0' }}>
            {(redirectToHoldLocationsNearbyTemplate ?? '').replace(
              '{{number}}',
              `${locations.length}`,
            )}
          </Paragraph>
          <List>
            {locations.slice(0, visibleListItemCount).map((l) => (
              <Item key={l.id}>
                <LocationItem
                  selected={l.id === selectedLocationId}
                  handleSelectItem={() => handleSelectItem(l)}
                  handleSelectLocation={() => handleChooseLocation(l)}
                  location={l}
                  selectLocationText={redirectToHoldSelectLocation ?? ''}
                  translations={locationHourItemTranslation}
                  featureName="redirectToHold"
                />
              </Item>
            ))}
          </List>
        </>
      );
      break;
    case COMPONENT_STATE.SUCCESS_NO_DATA:
      content = (
        <Paragraph containerCustomStyle={{ margin: '1rem 0 1.5rem 0' }}>
          {(redirectToHoldLocationsNearbyTemplate ?? '').replace(
            '{{number}}',
            '0',
          )}
        </Paragraph>
      );
      break;
    case COMPONENT_STATE.ERRORED:
      content = (
        <ErrorAlert
          show
          focusOnShow
          error
          dismissible={false}
          role="alert"
          timeout={0}
          s={{
            padding: '1rem',
            margin: '1rem 0',
            borderRadius: '.25rem',
          }}
        >
          <Paragraph containerCustomStyle={{ margin: 0, color: '#000' }}>
            {redirectToHoldSystemError}
          </Paragraph>
        </ErrorAlert>
      );
      break;
    default:
      ((x: never) => x)(componentState);
  }

  return (
    <div style={{ paddingTop: '0.5rem' }}>
      <PanelHeaderText>{redirectToHoldStep2}</PanelHeaderText>
      <ErrorAlert
        show={rthState.eligibilityWarning === 'onhold.best.effort'}
        focusOnShow
        error
        dismissible={false}
        role="alert"
        timeout={0}
        s={{
          padding: '1rem',
          margin: '0 0 2rem 0',
          borderRadius: '.25rem',
        }}
      >
        <Paragraph containerCustomStyle={{ margin: 0, color: '#000' }}>
          {redirectToHoldBestEffortWarning}
        </Paragraph>
      </ErrorAlert>
      <SemibigHeader as="h2">
        {redirectToHoldSelectLocationHeader}
      </SemibigHeader>
      {content}
      <ActionsRow>
        <CancelActionWrapper xs-columns={12} sm-columns={4}>
          <TextLink
            linkStyle={{
              sDefault: linksNoneDefault,
              sHover: linksNoneHover,
            }}
            onClick={handleCancel}
          >
            {redirectToHoldCancel}
          </TextLink>
        </CancelActionWrapper>
        {shouldShowExpandAction && (
          <ExpandActionWrapper xs-columns={12} sm-columns={4}>
            <TextLink
              linkStyle={{
                sDefault: linksTrackingDefault,
                sHover: linksTrackingHover,
              }}
              onClick={handleExpand}
            >
              {isListExpanded ? redirectToHoldShowLess : redirectToHoldShowMore}
            </TextLink>
          </ExpandActionWrapper>
        )}
        <FedexAttributionWrapper
          xs-columns={12}
          sm-columns={4}
          sm-column-offset={shouldShowExpandAction ? 0 : 4}
        >
          <FedexAttribution featureName="redirectToHold" />
        </FedexAttributionWrapper>
      </ActionsRow>
    </div>
  );
};

export default withTranslationsProvider(
  withThemeProvider(SelectLocationPanel, {
    themeTokens: [
      'linksNoneDefault',
      'linksNoneHover',
      'linksTrackingDefault',
      'linksTrackingHover',
      'smalltextDefaultNone',
    ],
  }),
  {
    translationKeys: [
      'redirectToHoldStep2',
      'redirectToHoldSelectLocationHeader',
      'redirectToHoldLocationsNearbyTemplate',
      'redirectToHoldSystemError',
      'redirectToHoldErrorMissingAddress',
      'redirectToHoldBestEffortWarning',
      'redirectToHoldVerify',
      'redirectToHoldCancel',
      'redirectToHoldShowLess',
      'redirectToHoldShowMore',
      'redirectToHoldSelectLocation',

      'redirectToHoldMonday',
      'redirectToHoldTuesday',
      'redirectToHoldWednesday',
      'redirectToHoldThursday',
      'redirectToHoldFriday',
      'redirectToHoldSaturday',
      'redirectToHoldSunday',
      'redirectToHoldClosedAllDay',
      'redirectToHoldOpenAllDay',
    ],
  },
);
